import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import { gr } from "../tokens";
import {
  grMeasureBoxSx,
  grMeasureInputSx,
  grMeasureUnitSx,
} from "./GrNumberStepper";

const split = (seconds) =>
  seconds == null || !Number.isFinite(Number(seconds))
    ? { min: "", sec: "" }
    : {
        min: String(Math.floor(Number(seconds) / 60)),
        sec: String(Math.round(Number(seconds) % 60)).padStart(2, "0"),
      };

const digits = (text, max) => String(text || "").replace(/\D/g, "").slice(0, max);

// Total seconds the boxes hold (null = both empty).
const toSeconds = (p) =>
  p.min === "" && p.sec === "" ? null : Number(p.min || 0) * 60 + Math.min(59, Number(p.sec || 0));

// Time taken (a run, a plank): minutes + seconds side by side.
// value / onChange are total seconds (null = empty); seconds above 59 are capped.
export default function GrDurationField({
  value,
  onChange,
  error = false,
  id,
  ariaLabel,
  sx,
}) {
  const [parts, setParts] = useState(() => split(value));
  // Value set from outside (loaded later, form reset) → show it; typing keeps the boxes as typed.
  useEffect(() => {
    const outside = value == null ? null : Number(value);
    setParts((p) => (toSeconds(p) === outside ? p : split(value)));
  }, [value]);
  const update = (next) => {
    setParts(next);
    return onChange(toSeconds(next));
  };
  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{ ...grMeasureBoxSx(error), ...sx }}
    >
      <InputBase
        id={id}
        value={parts.min}
        placeholder="0"
        inputProps={{ inputMode: "numeric", "aria-label": "Minutes" }}
        onChange={(e) => update({ ...parts, min: digits(e.target.value, 3) })}
        sx={grMeasureInputSx}
      />
      <Box component="span" sx={grMeasureUnitSx}>
        min
      </Box>
      <Box sx={{ width: "1px", background: gr.color.line }} />
      <InputBase
        value={parts.sec}
        placeholder="00"
        inputProps={{ inputMode: "numeric", "aria-label": "Seconds" }}
        // Seconds above 59 show as 59 (the value sent is capped too).
        onChange={(e) => {
          const sec = digits(e.target.value, 2);
          update({ ...parts, sec: Number(sec) > 59 ? "59" : sec });
        }}
        sx={grMeasureInputSx}
      />
      <Box component="span" sx={grMeasureUnitSx}>
        sec
      </Box>
    </Box>
  );
}

GrDurationField.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  id: PropTypes.string,
  ariaLabel: PropTypes.string,
  sx: PropTypes.object,
};
